<script setup>
import { Link } from '@inertiajs/vue3'
import ClickToActionSection from '@/components/ClickToActionSection.vue'
import { WrenchScrewdriverIcon, BoltIcon } from '@heroicons/vue/24/solid'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import ProjectCard from '@/pages/projects/partials/ProjectCard.vue'
import ServiceCard from '@/pages/services/Partials/ServiceCard.vue'

defineProps({
    projects: {
        type: Array,
        required: true,
    },

    serviceName: {
        type: String,
        required: true,
    },
})

const seo = {
    title: 'Topenářství',
    description:
        'Kompletní topenářské služby - instalace topných systémů, podlahové vytápění, tepelná čerpadla a servis kotlů. Energeticky úsporná řešení od firmy Kalianko.',
}

const services = [
    {
        name: 'Instalace topných systémů',
        description: 'Kompletní návrh a realizace vytápění pro domy, byty i komerční objekty.',
        icon: WrenchScrewdriverIcon,
        features: [
            'Instalace podlahového vytápění, radiátorů a plynových topidel',
            'Návrh a správné dimenzování systému',
            'Rovnoměrné rozložení tepla',
            'Důraz na energetickou úspornost',
            'Kvalitní komponenty a moderní technologie',
        ],
    },
    {
        name: 'Opravy a havarijní služba',
        description: 'Rychlé řešení poruch topení a havarijních situací.',
        icon: BoltIcon,
        features: [
            'Opravy radiátorů, prasklého potrubí a dalších komponentů',
            'Řešení problémů s regulací a výkonem',
            'Rychlý zásah při haváriích',
            'Minimalizace škod na majetku',
            'Obnova komfortu vytápění',
        ],
    },
]

const benefits = [
    { value: '24h', label: 'Havarijní služba' },
    { value: '30+', label: 'Let zkušeností' },
    { value: '100%', label: 'Záruka na práci' },
]
</script>

<template>
    <Head :title="seo.title">
        <meta
            name="description"
            :content="seo.description"
        />
    </Head>

    <section>
        <!-- Hero Section -->
        <div class="relative isolate overflow-hidden bg-gray-900">
            <img
                src="/images/kalianko-team.png"
                alt="Topenářství - profesionální topenářské služby"
                class="absolute inset-0 -z-10 size-full object-cover opacity-20"
            />
            <div class="absolute inset-0 -z-10 bg-linear-to-b from-gray-900/50 via-gray-900/80 to-gray-900" />

            <div class="mx-auto max-w-4xl px-6 py-32 text-center sm:py-40 lg:px-8 lg:py-56">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Topenářství</h1>

                <p class="mx-auto mt-6 max-w-2xl text-lg/8 text-gray-300">
                    Spolehlivé topenářské služby pro váš komfort. Od návrhu a instalace topných systémů po servis a
                    opravy kotlů. Postaráme se, abyste měli v zimě vždy teplo.
                </p>

                <div class="mt-10 flex flex-col items-center justify-center gap-2 lg:flex-row">
                    <Link
                        :href="contactIndex().url + '#demand-form'"
                        class="primary-link inline-flex w-full items-center justify-center rounded-full text-lg lg:w-fit"
                    >
                        Nezávazná poptávka
                    </Link>
                    <Link
                        :href="projectIndex().url + '?service=' + serviceName"
                        class="inline-flex w-full items-center justify-center rounded-full border border-white/30 bg-white/10 px-6 py-3 text-lg font-semibold text-white backdrop-blur-lg transition hover:bg-white/20 sm:w-auto"
                    >
                        Naše realizace
                    </Link>
                </div>

                <!-- Quick benefits -->
                <div class="mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-10">
                    <div
                        v-for="benefit in benefits"
                        :key="benefit.label"
                        class="text-center"
                    >
                        <div class="text-brand-primary text-3xl font-bold">{{ benefit.value }}</div>
                        <div class="mt-1 text-sm text-gray-400">{{ benefit.label }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Features Section -->
        <div class="section-width space-y-20 px-2 py-24 sm:py-32 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše služby</p>
                <h2 class="heading-2 mt-2">Co pro vás můžeme udělat</h2>
                <p class="paragraph mt-4">
                    Nabízíme kompletní topenářské služby od návrhu až po finální realizaci a následný servis. Každý
                    projekt řešíme individuálně s důrazem na efektivitu, spolehlivost a dlouhou životnost systému.
                    Pomůžeme vám zvolit optimální řešení vytápění s ohledem na typ objektu, energetickou náročnost i
                    vaše finanční možnosti.
                </p>
            </div>

            <div class="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
                <ServiceCard
                    v-for="service in services"
                    :key="service.name"
                    :service="service"
                />
            </div>

            <ClickToActionSection />

            <div
                v-if="projects.length > 0"
                class="space-y-5"
            >
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2 class="heading-2">Projekt z vytápění</h2>
                    <p class="paragraph mt-4">
                        Podívejte se na některé z našich nedávných realizací v oblasti vytápění. Každý projekt je pro
                        nás výzvou k předvedení našich schopností a odbornosti.
                    </p>
                </div>
                <div class="grid auto-rows-fr grid-cols-1 gap-8 lg:grid-cols-3">
                    <div
                        v-for="project in projects"
                        :key="project.id"
                        class="group"
                    >
                        <ProjectCard :project="project" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
